import { useState, useEffect, useRef, useCallback } from "react";
import { Award, Gift, CheckCircle, XCircle, Lock, ChevronDown, ChevronUp, FileText, Clock, History, Trophy, MapPin, Camera, Upload, LogIn, User } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ASN_LOGO = "https://customer-assets-jt897jd0.emergentagent.net/job_a9be0491-4fb8-4ce9-9cd4-b4a4d99b1ae7/artifacts/5k7vzzuw_ASN%20Trivia%20Logo%20White.png";

const getUserAuth = () => {
  const token = localStorage.getItem("userToken");
  const userData = JSON.parse(localStorage.getItem("userData") || "null");
  return { token, user: userData, isLoggedIn: !!token };
};

const TERMS_AND_CONDITIONS = `ASN Online Trivia Terms & Conditions

By participating in ASN Online Trivia, you agree to the following terms:

1. Participation is free and open to eligible individuals with an ASN account. No purchase is necessary.

2. Limit one prize winner per household during any rolling 20-day period, regardless of the number of participants, accounts, email addresses, or devices used by members of that household.

3. Winners are determined by the trivia results and any posted scoring or tie-breaking rules. ASN's decisions regarding answers, scoring, eligibility, and winners are final.

4. Potential winners may be required to verify their identity, age, contact information, and eligibility. Failure to respond within 2-3 business days may result in forfeiture of the prize.

5. Automated entries, multiple accounts, cheating, answer sharing, impersonation, or attempts to manipulate the trivia system are prohibited and may result in permanent disqualification.

6. Prizes are nontransferable and cannot be exchanged for cash unless otherwise stated. Winners are responsible for any taxes or expenses associated with accepting a prize.

7. ASN is not responsible for internet outages, website errors, delayed submissions, lost information, or other technical issues that may affect participation.

8. By accepting a prize, ASN admin(s) may reach out to the winner to use their first name, hometown, photograph, or likeness for promotional purposes unless prohibited by the winner.

9. ASN reserves the right to cancel, suspend, modify, or end any trivia contest; correct errors; revoke prizes obtained through rule violations; and disqualify any participant whose conduct is unfair, disruptive, or fraudulent.

10. Participation is void where prohibited by law. ASN may update these terms at any time, and continued participation constitutes acceptance of the current terms.`;


/* ── Countdown Timer ── */
const CountdownTimer = ({ expiresAt, onExpired }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const expiredRef = useRef(false);
  useEffect(() => {
    if (!expiresAt) return;
    expiredRef.current = false;
    const calc = () => {
      const diff = new Date(expiresAt) - new Date();
      if (diff <= 0) { setTimeLeft(0); if (!expiredRef.current) { expiredRef.current = true; onExpired?.(); } return; }
      setTimeLeft(diff);
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [expiresAt, onExpired]);
  if (!expiresAt || timeLeft === null) return null;
  if (timeLeft <= 0) return (
    <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 px-4 py-2.5" data-testid="trivia-timer-expired">
      <Clock className="w-4 h-4 text-red-400" /><span className="text-red-400 text-sm font-bold uppercase tracking-wider">Time's Up!</span>
    </div>
  );
  const h = Math.floor(timeLeft / 3600000), m = Math.floor((timeLeft % 3600000) / 60000), s = Math.floor((timeLeft % 60000) / 1000);
  const isUrgent = timeLeft < 300000;
  return (
    <div className={`flex items-center justify-between gap-3 ${isUrgent ? "bg-red-500/5 border-red-500/30" : "bg-blue-500/5 border-blue-500/20"} border px-4 py-2.5`} data-testid="trivia-countdown">
      <div className="flex items-center gap-2">
        <Clock className={`w-4 h-4 ${isUrgent ? "text-red-400 animate-pulse" : "text-blue-400"}`} />
        <span className={`${isUrgent ? "text-red-400" : "text-blue-400"} text-xs font-bold uppercase tracking-wider`}>Time Remaining</span>
      </div>
      <div className="flex items-center gap-1 font-mono">
        {h > 0 && <><span className={`${isUrgent ? "text-red-400" : "text-blue-400"} text-lg font-bold`}>{String(h).padStart(2, "0")}</span><span className="text-zinc-600 text-lg">:</span></>}
        <span className={`${isUrgent ? "text-red-400" : "text-blue-400"} text-lg font-bold`}>{String(m).padStart(2, "0")}</span>
        <span className="text-zinc-600 text-lg">:</span>
        <span className={`${isUrgent ? "text-red-400 animate-pulse" : "text-blue-400"} text-lg font-bold`}>{String(s).padStart(2, "0")}</span>
      </div>
    </div>
  );
};


/* ── Past Question Card ── */
const PastQuestionCard = ({ q }) => (
  <div className="border border-white/5 bg-zinc-900/30 p-4" data-testid={`trivia-history-${q.id}`}>
    <div className="flex items-start justify-between gap-3 mb-2">
      <div className="flex-1 min-w-0">
        {(q.title || q.presented_by_name) && (
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {q.title && <span className="text-zinc-500 text-xs font-bold uppercase">{q.title}</span>}
            {q.presented_by_name && <span className="text-orange-500/60 text-xs">Presented by {q.presented_by_name}</span>}
          </div>
        )}
        <p className="text-white/80 text-sm font-medium">{q.question}</p>
      </div>
      {q.prize && <span className="text-yellow-500/60 text-xs flex-shrink-0 whitespace-nowrap"><Gift className="w-3 h-3 inline mr-1" />{q.prize}</span>}
    </div>
    <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-white/5">
      <div className="flex items-center gap-2">
        <CheckCircle className="w-3.5 h-3.5 text-green-500/70" />
        <span className="text-green-400/80 text-sm font-bold">{q.correct_answer}</span>
      </div>
      <div className="flex items-center gap-3 text-zinc-600 text-xs">
        <span>{q.total_responses} response{q.total_responses !== 1 ? "s" : ""}</span>
        <span>{q.correct_responses} correct</span>
      </div>
    </div>
  </div>
);


/* ── Profile Setup Modal ── */
const ProfileSetupModal = ({ onComplete, userAuth }) => {
  const [hometown, setHometown] = useState("");
  const [consent, setConsent] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);

  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      setStream(s);
      setShowCamera(true);
    } catch { alert("Could not access camera."); }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
    canvas.toBlob(blob => {
      setPhotoFile(new File([blob], "camera_photo.jpg", { type: "image/jpeg" }));
      setPhotoPreview(URL.createObjectURL(blob));
      stopCamera();
    }, "image/jpeg", 0.85);
  };

  const stopCamera = () => {
    stream?.getTracks().forEach(t => t.stop());
    setStream(null);
    setShowCamera(false);
  };

  useEffect(() => {
    if (showCamera && videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [showCamera, stream]);

  useEffect(() => () => { stream?.getTracks().forEach(t => t.stop()); }, [stream]);

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f) {
      setPhotoFile(f);
      setPhotoPreview(URL.createObjectURL(f));
    }
  };

  const handleSave = async () => {
    if (!hometown.trim()) { alert("Please enter your hometown."); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${userAuth.token}` };
      await axios.post(`${API}/trivia/profile`, { hometown: hometown.trim(), social_media_consent: consent }, { headers });
      if (photoFile && consent) {
        const fd = new FormData();
        fd.append("file", photoFile);
        await axios.post(`${API}/trivia/profile/photo`, fd, { headers, "Content-Type": "multipart/form-data" });
      }
      onComplete();
    } catch (err) { alert(err.response?.data?.detail || "Error saving profile"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="trivia-profile-modal">
      <div className="bg-zinc-900 border border-white/10 max-w-md w-full p-6 space-y-5">
        <div className="text-center">
          <User className="w-10 h-10 text-orange-500 mx-auto mb-2" />
          <h2 className="text-white font-bold text-lg uppercase tracking-tight">Set Up Your Trivia Profile</h2>
          <p className="text-zinc-500 text-sm mt-1">Tell us a bit about yourself to get started!</p>
        </div>

        <div>
          <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">Hometown *</label>
          <input value={hometown} onChange={e => setHometown(e.target.value)} placeholder="e.g. Tyler, TX" className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2.5 text-sm focus:border-orange-500/50 outline-none" data-testid="trivia-profile-hometown" />
        </div>

        <div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="accent-orange-500 mt-1" data-testid="trivia-profile-consent" />
            <span className="text-zinc-400 text-sm">I consent to being featured on ASN social media if I win</span>
          </label>
        </div>

        {consent && (
          <div className="space-y-3">
            <label className="text-zinc-400 text-xs uppercase tracking-wider block">Photo (optional — for social media posts)</label>
            {photoPreview ? (
              <div className="relative w-24 h-24 mx-auto">
                <img src={photoPreview} alt="Preview" className="w-24 h-24 rounded-full object-cover border-2 border-orange-500" />
                <button onClick={() => { setPhotoFile(null); setPhotoPreview(null); }} className="absolute -top-1 -right-1 bg-red-500 text-white w-5 h-5 rounded-full text-xs flex items-center justify-center">×</button>
              </div>
            ) : showCamera ? (
              <div className="space-y-2">
                <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-48 bg-black rounded" />
                <div className="flex gap-2 justify-center">
                  <button type="button" onClick={capturePhoto} className="px-4 py-2 bg-orange-500 text-black font-bold text-xs uppercase">Capture</button>
                  <button type="button" onClick={stopCamera} className="px-4 py-2 bg-zinc-700 text-white text-xs uppercase">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex gap-3 justify-center">
                <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 px-4 py-2 bg-zinc-800 border border-white/10 text-zinc-300 text-xs uppercase hover:border-white/30">
                  <Upload className="w-3.5 h-3.5" /> Upload
                </button>
                <button type="button" onClick={startCamera} className="flex items-center gap-2 px-4 py-2 bg-zinc-800 border border-white/10 text-zinc-300 text-xs uppercase hover:border-white/30">
                  <Camera className="w-3.5 h-3.5" /> Camera
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
              </div>
            )}
          </div>
        )}

        <button onClick={handleSave} disabled={saving} className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-black font-bold text-sm uppercase tracking-wider" data-testid="trivia-profile-save">
          {saving ? "Saving..." : "Start Playing"}
        </button>
      </div>
    </div>
  );
};


/* ── Leaderboard Section ── */
const LeaderboardSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/trivia/leaderboard`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data?.season) return null;

  const { season, leaderboard } = data;
  const now = new Date();
  const end = new Date(season.end_date);
  const isEnded = now > end;

  return (
    <div data-testid="trivia-leaderboard-section">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h2 className="font-secondary text-lg font-bold text-white uppercase tracking-tight">{season.name}</h2>
        </div>
        <span className={`text-xs font-bold uppercase px-2 py-0.5 ${isEnded ? "bg-red-500/20 text-red-400" : "bg-green-500/20 text-green-400"}`}>
          {isEnded ? "Season Ended" : "Active Season"}
        </span>
      </div>
      <p className="text-zinc-500 text-xs mb-4">
        {new Date(season.start_date).toLocaleDateString()} — {new Date(season.end_date).toLocaleDateString()}
      </p>
      {leaderboard.length === 0 ? (
        <p className="text-zinc-600 text-sm text-center py-6">No trivia results yet this season.</p>
      ) : (
        <div className="border border-white/5 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-900/80 border-b border-white/10">
                <th className="text-left text-zinc-500 text-[10px] uppercase tracking-wider py-2.5 px-3 w-12">#</th>
                <th className="text-left text-zinc-500 text-[10px] uppercase tracking-wider py-2.5 px-3">Player</th>
                <th className="text-right text-zinc-500 text-[10px] uppercase tracking-wider py-2.5 px-3">Correct</th>
                <th className="text-right text-zinc-500 text-[10px] uppercase tracking-wider py-2.5 px-3">Played</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.slice(0, 25).map(entry => (
                <tr key={entry.user_id} className="border-b border-white/5 hover:bg-zinc-900/50">
                  <td className="py-2.5 px-3">
                    <span className={`font-bold text-sm ${entry.rank <= 3 ? "text-yellow-500" : "text-zinc-500"}`}>
                      {entry.rank}
                    </span>
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-2">
                      {entry.photo_url && entry.social_media_consent ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${entry.photo_url}`} alt="" className="w-6 h-6 rounded-full object-cover" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center">
                          <User className="w-3 h-3 text-zinc-500" />
                        </div>
                      )}
                      <div>
                        <span className="text-white text-sm font-medium">{entry.name || entry.username}</span>
                        {entry.hometown && <span className="text-zinc-600 text-xs ml-2"><MapPin className="w-2.5 h-2.5 inline" /> {entry.hometown}</span>}
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-right text-green-400 font-bold">{entry.correct}</td>
                  <td className="py-2.5 px-3 text-right text-zinc-500">{entry.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


/* ── Main Page ── */
export const TriviaPage = () => {
  const [trivia, setTrivia] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);
  const [answer, setAnswer] = useState("");

  const [userAuth, setUserAuth] = useState(getUserAuth);
  const [triviaProfile, setTriviaProfile] = useState(null);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [profileChecked, setProfileChecked] = useState(false);

  const refreshAuth = useCallback(() => setUserAuth(getUserAuth()), []);

  // Check for auth changes (e.g. user logs in on another tab)
  useEffect(() => {
    const handler = () => refreshAuth();
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [refreshAuth]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [activeRes, historyRes] = await Promise.all([
          axios.get(`${API}/trivia/active`),
          axios.get(`${API}/trivia/history`)
        ]);
        setTrivia(activeRes.data);
        setHistory(historyRes.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchData();
  }, []);

  // Check trivia profile if logged in
  useEffect(() => {
    if (!userAuth.isLoggedIn) { setProfileChecked(true); return; }
    axios.get(`${API}/trivia/profile`, { headers: { Authorization: `Bearer ${userAuth.token}` } })
      .then(res => {
        if (res.data.has_profile) setTriviaProfile(res.data.profile);
        else setShowProfileSetup(true);
      })
      .catch(() => {})
      .finally(() => setProfileChecked(true));
  }, [userAuth]);

  const handleTimerExpired = () => {
    setTimerExpired(true);
    axios.get(`${API}/trivia/active`).then(res => setTrivia(res.data));
    axios.get(`${API}/trivia/history`).then(res => setHistory(res.data));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) { setError("You must agree to the Terms & Conditions to participate."); return; }
    if (!answer.trim()) { setError("Please provide an answer."); return; }
    setError("");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/trivia/submit`, { answer: answer.trim() }, {
        headers: { Authorization: `Bearer ${userAuth.token}` }
      });
      setResult(res.data);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong.");
    }
    setSubmitting(false);
  };

  const handleProfileComplete = () => {
    setShowProfileSetup(false);
    // Reload profile
    axios.get(`${API}/trivia/profile`, { headers: { Authorization: `Bearer ${userAuth.token}` } })
      .then(res => { if (res.data.has_profile) setTriviaProfile(res.data.profile); });
  };

  if (loading || !profileChecked) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-8 h-8 border-2 border-white/20 border-t-orange-500 rounded-full animate-spin" /></div>;
  }

  const question = trivia?.question;
  const isActive = trivia?.active;
  const isLocked = question?.is_locked || timerExpired;
  const pastQuestions = history.filter(h => !question || h.id !== question.id);

  // Determine hero logo
  const heroLogo = (question?.presented_by_logo_url) || ASN_LOGO;

  return (
    <div className="min-h-[60vh] bg-[#0a0a0a]" data-testid="trivia-page">
      {showProfileSetup && userAuth.isLoggedIn && (
        <ProfileSetupModal onComplete={handleProfileComplete} userAuth={userAuth} />
      )}

      {/* Hero Section */}
      <div className="bg-gradient-to-b from-zinc-900/80 to-transparent border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 py-10 md:py-14 text-center">
          <div className="mb-5">
            <img src={heroLogo} alt="Trivia" className="h-20 md:h-28 object-contain mx-auto" data-testid="trivia-hero-logo" />
          </div>
          <h1 className="font-secondary text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-white mb-3" data-testid="trivia-page-title">
            Online Trivia
          </h1>
          <p className="text-zinc-400 text-sm md:text-base max-w-lg mx-auto">
            Answer correctly for a chance to win. One active question at a time — get it right!
          </p>
          {question?.presented_by_name && (
            <p className="text-zinc-500 text-xs mt-3 uppercase tracking-wider">
              Presented by <span className="text-orange-500 font-bold">{question.presented_by_name}</span>
            </p>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 md:py-12 space-y-12">

        {/* ═══ ACTIVE QUESTION SECTION ═══ */}
        {!isActive ? (
          <div className="text-center py-16 bg-zinc-900/30 border border-white/5" data-testid="trivia-no-question">
            <Award className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="font-secondary text-xl font-bold text-white uppercase tracking-tight mb-2">No Active Trivia</h2>
            <p className="text-zinc-500 text-sm">Check back soon for the next trivia question!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Title bar */}
            {question.title && (
              <div className="bg-zinc-900/50 border border-white/5 px-5 py-3" data-testid="trivia-header">
                <span className="text-white font-bold text-sm uppercase tracking-wider">{question.title}</span>
              </div>
            )}

            {/* Prize */}
            {question.prize && (
              <div className="flex items-center gap-3 bg-yellow-500/5 border border-yellow-500/20 px-5 py-3" data-testid="trivia-prize">
                <Gift className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                <div>
                  <span className="text-yellow-500 text-xs font-bold uppercase tracking-wider block">Prize</span>
                  <span className="text-white text-sm font-medium">{question.prize}</span>
                </div>
              </div>
            )}

            {/* Countdown Timer */}
            {question.expires_at && !isLocked && !submitted && (
              <CountdownTimer expiresAt={question.expires_at} onExpired={handleTimerExpired} />
            )}

            {/* Question Card */}
            <div className={`border ${isLocked ? "border-zinc-700 bg-zinc-900/80" : "border-orange-500/20 bg-zinc-900"} p-6`} data-testid="trivia-question-card">
              {isLocked && (
                <div className="flex items-center gap-2 mb-4 text-zinc-400">
                  <Lock className="w-4 h-4" /><span className="text-xs font-bold uppercase tracking-wider">This question is closed</span>
                </div>
              )}

              <h2 className="text-white text-lg md:text-xl font-bold mb-6" data-testid="trivia-question-text">{question.question}</h2>

              {isLocked && question.correct_answer && (
                <div className="bg-green-500/10 border border-green-500/20 p-4 mb-4" data-testid="trivia-correct-answer-display">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 text-xs font-bold uppercase tracking-wider">Correct Answer</span>
                  </div>
                  <p className="text-green-300 text-base font-bold">{question.correct_answer}</p>
                </div>
              )}

              {isLocked && (
                <div className="flex items-center gap-4 text-zinc-500 text-xs">
                  <span>{question.total_responses} total response{question.total_responses !== 1 ? "s" : ""}</span>
                  <span>{question.correct_responses} answered correctly</span>
                </div>
              )}

              {/* LOGIN GATE */}
              {!isLocked && !submitted && !userAuth.isLoggedIn && (
                <div className="text-center py-8 space-y-4" data-testid="trivia-login-gate">
                  <LogIn className="w-12 h-12 text-zinc-600 mx-auto" />
                  <p className="text-zinc-400 text-sm">Sign in with your ASN account to submit your answer.</p>
                  <div className="flex gap-3 justify-center">
                    <Link to="/login" className="px-6 py-2.5 bg-orange-500 hover:bg-orange-600 text-black font-bold text-sm uppercase tracking-wider inline-block" data-testid="trivia-login-btn">Sign In</Link>
                    <Link to="/signup" className="px-6 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-sm uppercase tracking-wider border border-white/10 inline-block" data-testid="trivia-signup-btn">Create Account</Link>
                  </div>
                </div>
              )}

              {/* ANSWER FORM (logged-in users) */}
              {!isLocked && !submitted && userAuth.isLoggedIn && (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="trivia-submit-form">
                  {triviaProfile && (
                    <div className="flex items-center gap-2 bg-zinc-800/50 px-3 py-2 border border-white/5 text-zinc-400 text-xs mb-2">
                      <User className="w-3.5 h-3.5" />
                      <span>Playing as <span className="text-white font-medium">{userAuth.user?.name || userAuth.user?.username}</span></span>
                      {triviaProfile.hometown && <span className="text-zinc-600">• {triviaProfile.hometown}</span>}
                    </div>
                  )}

                  <div>
                    <label className="text-zinc-500 text-xs uppercase tracking-wider block mb-2">Your Answer *</label>
                    {question.type === "multiple_choice" ? (
                      <div className="space-y-2" data-testid="trivia-mc-options">
                        {question.options.map((opt, i) => (
                          <label key={i} onClick={() => setAnswer(opt)} className={`flex items-center gap-3 p-3 border cursor-pointer transition-all ${answer === opt ? "border-orange-500 bg-orange-500/10" : "border-white/10 bg-zinc-800 hover:border-white/20"}`} data-testid={`trivia-mc-option-${i}`}>
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${answer === opt ? "border-orange-500" : "border-zinc-600"}`}>
                              {answer === opt && <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />}
                            </div>
                            <span className="text-white text-sm">{opt}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <input value={answer} onChange={e => setAnswer(e.target.value)} required placeholder="Type your answer here..." className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2.5 text-sm focus:border-orange-500/50 outline-none" data-testid="trivia-text-answer-input" />
                    )}
                  </div>

                  <div className="pt-1">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={agreedToTerms} onChange={e => { setAgreedToTerms(e.target.checked); if (error) setError(""); }} className="accent-orange-500 mt-1" data-testid="trivia-terms-checkbox" />
                      <span className="text-zinc-400 text-xs">
                        I agree to the{" "}
                        <button type="button" onClick={() => setShowTerms(!showTerms)} className="text-orange-500 hover:text-orange-400 underline">Terms & Conditions</button>
                      </span>
                    </label>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/20 px-4 py-2" data-testid="trivia-error">
                      <XCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
                    </div>
                  )}

                  <button type="submit" disabled={submitting || (!answer && question.type === "multiple_choice")}
                    className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold text-sm uppercase tracking-wider transition-colors"
                    data-testid="trivia-submit-btn"
                    onClick={(e) => { if (question.type === "multiple_choice" && !answer) { e.preventDefault(); setError("Please select an answer."); } }}
                  >
                    {submitting ? "Submitting..." : "Submit Answer"}
                  </button>
                </form>
              )}

              {/* Result */}
              {submitted && result && (
                <div className={`p-6 text-center ${result.is_correct ? "bg-green-500/10 border border-green-500/20" : "bg-red-500/10 border border-red-500/20"}`} data-testid="trivia-result">
                  <div className="mb-3">{result.is_correct ? <CheckCircle className="w-12 h-12 text-green-400 mx-auto" /> : <XCircle className="w-12 h-12 text-red-400 mx-auto" />}</div>
                  <h3 className={`text-lg font-bold mb-2 ${result.is_correct ? "text-green-400" : "text-red-400"}`}>
                    {result.is_correct ? "Correct!" : "Incorrect"}
                  </h3>
                  <p className="text-zinc-400 text-sm">{result.message}</p>
                  {result.is_correct && question.prize && (
                    <p className="text-yellow-400 text-sm mt-3 font-medium"><Gift className="w-4 h-4 inline mr-1" />You're in the running for: {question.prize}</p>
                  )}
                </div>
              )}

              {!isLocked && submitted && (
                <p className="text-zinc-500 text-xs text-center mt-4">Your answer has been recorded. Thank you for participating!</p>
              )}
            </div>

            {/* Terms */}
            <div className="border border-white/5 bg-zinc-900/30" data-testid="trivia-terms-section">
              <button onClick={() => setShowTerms(!showTerms)} className="flex items-center justify-between w-full px-5 py-3 text-left">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-zinc-500" />
                  <span className="text-zinc-400 text-xs font-bold uppercase tracking-wider">Terms & Conditions</span>
                </div>
                {showTerms ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
              </button>
              {showTerms && (
                <div className="px-5 pb-5 border-t border-white/5 pt-4">
                  <pre className="text-zinc-500 text-xs leading-relaxed whitespace-pre-wrap font-sans" data-testid="trivia-terms-text">{TERMS_AND_CONDITIONS}</pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ LEADERBOARD ═══ */}
        <LeaderboardSection />

        {/* ═══ PAST QUESTIONS ═══ */}
        {pastQuestions.length > 0 && (
          <div data-testid="trivia-history-section">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-5 h-5 text-zinc-500" />
              <h2 className="font-secondary text-lg font-bold text-white uppercase tracking-tight">Past Trivia</h2>
              <span className="text-zinc-600 text-xs">({pastQuestions.length})</span>
            </div>
            <div className="space-y-2">
              {pastQuestions.map(q => <PastQuestionCard key={q.id} q={q} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TriviaPage;
